import * as React from 'react'
import { useService } from '../../../hooks/use-service'
import { CampaignV2Service } from '../../../services'
import { useAxiosCancelToken } from '../../../hooks/use-axios-cancel-token'
import { useDebouncer } from '@pushly/aqe/lib/hooks'

import { ISchedulableLike, OccurrenceDTO } from '../types'

export interface IUseNextOccurrencesCoreInput {
  schedule: ISchedulableLike | any
  timeZone?: string
}

export interface IUseNextOccurrencesCoreOutput {
  occurrences: OccurrenceDTO[]
  loading: boolean
  currTz: string
}

function trySerializeSchedule(schedule: any): any {
  try {
    if (schedule && typeof schedule.serialize === 'function') {
      return schedule.serialize()
    }
  } catch (_e) {
    // fall through to raw schedule
  }
  return schedule ?? {}
}

function tryGetTimeZone(schedule: any, override?: string): string {
  try {
    if (schedule && typeof schedule.getTimeZone === 'function') {
      return schedule.getTimeZone() ?? (override || 'UTC')
    }
    if (schedule && typeof schedule.timeZone === 'string') {
      return schedule.timeZone
    }
  } catch (_e) {
    // ignore and use fallback
  }
  // Prefer explicit override next, then default to UTC
  return override || 'UTC'
}

function tryGetDateRange(schedule: any): { dateStart?: string; dateEnd?: string } {
  let dateStart: string | undefined
  let dateEnd: string | undefined

  try {
    if (schedule && typeof schedule.getDateStart === 'function') {
      dateStart = schedule.getDateStart()
    } else if (typeof schedule?.dateStart === 'string') {
      dateStart = schedule.dateStart
    }
  } catch (_e) {}

  try {
    if (schedule && typeof schedule.getDateEnd === 'function') {
      dateEnd = schedule.getDateEnd()
    } else if (typeof schedule?.dateEnd === 'string') {
      dateEnd = schedule.dateEnd
    }
  } catch (_e) {}

  return { dateStart, dateEnd }
}

export function useNextOccurrencesCore({ schedule, timeZone }: IUseNextOccurrencesCoreInput): IUseNextOccurrencesCoreOutput {
  const svc = useService<CampaignV2Service>(CampaignV2Service)
  const [cancelToken, refreshCancelToken] = useAxiosCancelToken()
  const [debounce] = useDebouncer()

  const [occurrences, setOccurrences] = React.useState<OccurrenceDTO[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)

  const currTz = tryGetTimeZone(schedule, timeZone)

  React.useEffect(() => {
    setLoading(true)
    refreshCancelToken()

    const serialized = trySerializeSchedule(schedule)
    const { dateStart, dateEnd } = tryGetDateRange(schedule)

    debounce(() =>
      svc
        .fetchNextOccurrencesByQuery(
          {
            dateStart,
            dateEnd,
            configuration: { schedule: serialized },
          },
          { cancellationToken: cancelToken.current },
        )
        .then((res) => {
          setOccurrences(res.data ?? [])
          setLoading(!!(res as any)?.cancelled)
        })
        .catch(() => setLoading(false)),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(trySerializeSchedule(schedule))])

  return { occurrences, loading, currTz }
}
